import styled, { keyframes } from 'styled-components';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';

export const ChatSectionStyled = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
  min-height: 0;
`;

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

export const ChatColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  min-height: 0;
  min-width: 0;
  overflow: hidden;

  &.poly-chat-layout {
    height: 100%;
  }

  .poly-chat-messages {
    flex: 1;
    min-height: 0;
    min-width: 0;
    height: auto !important;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 24px;

    .heading {
      overflow: visible;
      line-height: normal;
    }

    .user-text {
      align-items: flex-end;
      overflow: visible;

      .icon-box {
        align-self: flex-end;
      }
    }
  }

  .poly-chat-empty {
    height: 100%;
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .poly-chat-thread {
    max-width: 48rem;
    margin: 0 auto;
    width: 100%;
    min-width: 0;
    overflow: visible;
  }

  .poly-chat-input {
    flex-shrink: 0;
    padding: 16px 24px 20px;
    border-top: 1px solid var(--line);
    background: var(--paper);

    .poly-chat-search {
      max-width: 48rem;
      margin: 0 auto;
    }
  }

  .error-text {
    color: var(--danger);
  }

  .chat-loader-text {
    display: block;
    font-weight: 400;
    margin-bottom: 10px;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }

  .text-block {
    flex-grow: 1;
    height: 100vh;
    overflow: auto;
    padding: 15px 5px 15px 0;

    .user-text {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      position: relative;
      padding-right: 0;
      margin-bottom: 24px;

      .icon-box {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
      }

      .title {
        max-width: 80%;
        margin: 0;
        padding: 12px 16px;
        border-radius: 16px 16px 4px 16px;
        background: rgba(123, 135, 148, 0.12);
        border: 1px solid rgba(123, 135, 148, 0.28);
        color: var(--ink);
        font-weight: 400;
        font-size: 14px;
        line-height: 1.5;
        white-space: pre-line;
      }

      .see-source-button {
        position: static;
        flex-shrink: 0;
        margin: 0;
        padding: 5px 10px;
        color: var(--mute);
        background: var(--paper-2);
        border: 1px solid var(--line);
        border-radius: 9999px;
        font-size: 12px;
        line-height: 1.2;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
          color: var(--ink);
          border-color: var(--space);
        }
      }
    }

    .heading {
      margin: 0 0 24px;
      position: relative;
      padding-left: 0;
      line-height: 28px;
      max-width: 100%;
      min-width: 0;
      overflow-x: hidden;

      &:not(.user-text) {
        .icon-box {
          display: none;
        }

        .ai-message-content,
        .streaming-text {
          max-width: 100%;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
          padding: 16px 18px;
          border-radius: 22px;
          background: #1c1c1f;
          border: 1px solid #2E2E35;
          box-shadow: var(--shadow-soft);
          color: var(--ink);
          font-size: 15px;
          line-height: 1.55;
          overflow-x: hidden;
          overflow-wrap: anywhere;
          word-break: break-word;

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            color: var(--ink);
          }

          p,
          li,
          span {
            color: var(--ink);
          }

          strong,
          b {
            color: var(--ink);
          }

          em {
            color: var(--mute);
          }

          th {
            color: var(--ink);
          }

          td {
            color: var(--ink);
          }

          blockquote {
            color: var(--mute);
            border-left: 2px solid var(--space);
            background: var(--space-soft);
          }

          code {
            color: var(--space);
          }

          pre code {
            color: var(--ink);
          }

          hr {
            border-color: var(--line);
          }
        }
      }

      .ai-message-content {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      .streaming-text {
        white-space: pre-wrap;
        word-break: break-word;
        line-height: 1.6;
        color: var(--ink);
      }

      a {
        text-decoration: underline;
        color: var(--talent);
      }

      .icon-box {
        position: absolute;
        left: 0;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        font-size: 20px;
        line-height: 23px;
        font-weight: 700;
        text-transform: uppercase;
        border-radius: 50px;
        color: var(--text-color);
        background: var(--white);
        &--purple {
          background: rgb(135 137 147 / 48%);
          overflow: hidden;
        }
      }

      /* Poly chat: keep avatar in normal flow so it is never clipped */
      &.poly-chat-messages .heading .icon-box,
      &.poly-chat-messages .user-text .icon-box {
        position: static !important;
        top: auto !important;
        left: auto !important;
        display: flex !important;
        width: 36px;
        height: 36px;
        margin: 0 0 8px;
        font-size: 14px;
        line-height: 1;
        color: #0B0B0D;
        background: #ffffff;
        border-radius: 50%;
      }

      &.poly-chat-messages .heading:not(.user-text) .icon-box {
        display: none !important;
      }

      .chat-table-wrap {
        display: block;
        width: 100%;
        max-width: 100%;
        margin: 12px 0;
        overflow-x: auto;
        overflow-y: hidden;
        overscroll-behavior-x: contain;
        border-radius: 12px;
        border: 1px solid var(--line);
        background: var(--paper);
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
          height: 8px;
        }
        &::-webkit-scrollbar-track {
          background: var(--paper-2);
        }
        &::-webkit-scrollbar-thumb {
          background: var(--mute);
          border-radius: 4px;
        }
      }

      table {
        border-collapse: collapse;
        width: 100%;
        table-layout: fixed;
      }

      .chat-table-wrap > table {
        width: 100%;
        min-width: 100%;
        table-layout: fixed;
      }

      th,
      td {
        border: 1px solid var(--line);
        border-spacing: 0;
        padding: 10px 12px;
        text-align: left;
        vertical-align: top;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        hyphens: auto;
        min-width: 0;
        color: var(--ink);
      }

      th {
        font-weight: 800;
        background: var(--paper-2);
        color: var(--ink);
      }

      .lucide-download {
        display: none;
      }

      .title {
        display: block;
        margin: 0;
      }

      pre {
        background-color: var(--paper-2);
        color: var(--ink);
        padding: 16px;
        border-radius: 12px;
        font-family: var(--base-font-mono);
        font-size: 14px;
        line-height: 22px;
        margin-top: 10px;
        margin-bottom: 10px;
        border: 1px solid var(--line);

        &::-webkit-scrollbar {
          height: 5px;
        }
        &::-webkit-scrollbar-thumb {
          background: var(--mute);
          border-radius: 4px;
        }
        &::-webkit-scrollbar-thumb:hover {
          background: var(--ink);
        }
      }
    }

    .title {
      display: block;
      font-size: 20px;
      line-height: 24px;
      font-weight: 700;
      margin: 0 0 20px;
    }

    .text-box {
      width: 100%;
      margin: 0 0 20px;
    }

    .detail-box {
      width: 100%;
      padding: 20px 0;
      border-top: 1px solid #cdcdcd;

      .list {
        margin: 0;
        padding: 0 0 0 15px;

        li {
          margin: 0 0 15px;
        }
      }
    }

    .reponse-block {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      .agent-name {
        display: block;
        font-size: 16px;
        line-height: 20px;
        font-weight: 600;
        color: var(--ink);
        padding: 8px 15px;
        border-radius: 10px;
        background: var(--paper-2);
        border: 1px solid var(--line);
      }

      .action-list {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;

        li {
          margin: 0;
        }

        button {
          font-size: 22px;
          line-height: 1;
          color: var(--light-gray);

          &:hover {
            color: var(--text-color);
          }
        }
      }
    }
  }

  .swiper-holder {
    position: relative;
    width: 100%;
    max-width: 100%;
    margin-bottom: 8px;

    .swiper {
      padding-top: 8px;
      margin-bottom: 12px;
      overflow: hidden;

      .swiper-slide {
        height: auto;
        box-sizing: border-box;

        .img-wrap {
          display: block;
          width: 100%;
          height: 140px;
          overflow: hidden;
          border-radius: 10px;
          border: 1px solid var(--line);
          background: var(--paper-2);

          @media (min-width: 768px) {
            height: 180px;
          }

          img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }

    .swiper-controls {
      display: flex;
      gap: 10px;
      pointer-events: none;
    }

    .swiper-controls button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 8px;
      left: auto;
      z-index: 2;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--ink);
      color: var(--paper);
      border-radius: 50%;
      border: 1px solid var(--line);
      cursor: pointer;
      pointer-events: auto;
      box-shadow: var(--shadow-soft);

      &:hover {
        background: var(--space);
        color: var(--paper);
      }

      &::after {
        display: none;
      }

      svg {
        width: 18px;
        height: 18px;
      }
    }

    .swiper-button-prev {
      right: auto !important;
      left: 8px !important;
    }
  }
`;

export const SearchResultCol = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  overflow: auto;
  flex-shrink: 0;
  width: 300px;
  padding: 10px 20px;
  border-left: 1px solid var(--line);
  background: var(--paper);
  z-index: 999;

  @media (min-width: 1200px) {
    width: 500px;
  }
  .content-box {
    box-shadow: var(--shadow-soft);
    padding: 8px 16px;
    border-radius: 12px;
    margin-bottom: 10px;
    transition: 0.3s all ease-in-out;
    border: 1px solid var(--line);
    background: var(--paper-2);
    &:hover {
      border: 1px solid var(--space);
      box-shadow: var(--shadow-soft);
    }
  }

  .tablist {
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
    display: flex;
    gap: 15px;
    position: relative;
    padding-right: 35px;

    @media (min-width: 1400px) {
      gap: 30px;
    }
    .source-close {
      width: 20px;
      height: 20px;
      border: 1px solid var(--danger);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      position: absolute;
      right: 5px;
      top: 5px;
    }
    li {
      margin: 0;

      button {
        display: block;
        font-size: 16px;
        line-height: 20px;
        text-transform: capitalize;
        padding: 0 0 5px;
        color: var(--ink);
        border-bottom: 2px solid transparent;

        @media (min-width: 1400px) {
          font-size: 18px;
          line-height: 22px;
        }

        &.active {
          font-weight: 700;
          border-bottom: 2px solid var(--white);
        }
      }
    }
  }

  .tab-content {
    position: relative;

    .imgs-wrap,
    .video-wrap {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
    }
  }

  .content-box {
    position: relative;
    font-size: 14px;
    line-height: 18px;
    cursor: pointer;

    .head {
      margin: 0 0 10px;
      display: flex;
      align-items: center;
      gap: 10px;

      .img-box {
        width: 22px;
        flex-shrink: 0;
      }

      .text {
        font-size: 14px;
        line-height: 18px;
        display: block;
        color: var(--ink);
        max-width: 200px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    .text-box {
      width: 100%;
    }
    .title {
      display: block;
      font-size: 16px;
      line-height: 20px;
      font-weight: 700;
      margin: 0 0 10px;
    }

    .detail-holder {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 400px;
      padding: 15px;
      border-left: 1px solid #cdcdcd;
      border-radius: 10px 0 0 10px;
      background: var(--white);
      transform: translateX(102%);
      transition: all 0.3s ease-in-out;
      z-index: 72;

      .detail-active & {
        transform: translateX(0);
      }

      @media screen and (min-width: 1600px) {
        width: 500px;
      }

      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin: 0 0 15px;

        .title {
          font-size: 18px;
          line-height: 22px;
          font-weight: 700;
          margin: 0;
        }

        .btn-close {
          width: 25px;
          height: 25px;
          border: 1px solid var(--text-color);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
      }

      .text-area {
        width: 100%;
        height: calc(100vh - 70px);
        overflow: auto;
        padding-right: 5px;
      }
    }
  }
  .block-heading {
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    margin-bottom: 20px;
    color: var(--text-color-50);
  }

  .imgs-holder {
    max-width: 100%;
    overflow-x: auto;
    .img-wrap {
      width: 100%;
      height: 200px;
      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        margin-bottom: 20px;
      }
    }
  }
`;

export const ReponseMenu = styled(Menu)`
  ul {
    width: 300px;
    padding: 10px;
    border-radius: 20px;
    border: 3px solid #32323A;
    box-shadow: 0 0 10px #43434C;
  }
`;
export const MenuBtn = styled(MenuButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 50px;
  padding: 10px 6px;
  font-size: 14px;
  line-height: 17px;
  font-weight: 500;
  text-transform: capitalize;
  border-radius: 12px;
  border: 1px solid #43434C;
  background: #f1f1f1;

  .icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    border-radius: 12px;
    background: rgba(26, 42, 108, 0.1);
  }
`;
export const StyleItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px;
  margin: 0 0 6px;
  border-radius: 10px;
  border: 1px solid transparent;

  &.active,
  &:hover {
    border-color: var(--text-color);
    background: rgba(123, 135, 148, 0.1);
  }

  &:last-child {
    margin: 0;
  }

  .wrap {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .icon-box {
    width: 35px;
  }

  .pro {
    display: block;
    min-width: 50px;
    padding: 5px;
    font-size: 14px;
    line-height: 17px;
    text-transform: capitalize;
    color: var(--text-color);
    text-align: center;
    border-radius: 10px;
    background: var(--primary);
  }
`;
